import { useParams } from 'react-router-dom';
import { Activity } from 'react';
import { useAppSelector } from '@store';
import { v4 as uuid } from 'uuid';
import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  ChangeLogs,
  IconExpand,
  PermissionDenied,
  PermissionEnum,
  Popup,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  Tabs,
} from '@ui/src';
import { CustomerStatus, OrganizationType } from '../../types';
import useExternalAccountForm from '../hooks/use-external-account-form';
import ExternalAccountFormContent from '../../../../components/common/external-accounts/form';
import LinkCustomerForm from '../../linked-clients/form';
import ExternalAccountTopUpOptionItem from '../../../../components/common/external-accounts/top-op-option';

import cn from '../styles.module.css';
import { ExternalAccountTopUpOptionFormValues } from '../types';

const ExternalAccountForm = ({ type }: { type: OrganizationType }) => {
  const { accountId: externalAccountId, customerId } = useParams();

  const { casp } = useAppSelector((state) => state.customersDictionaries);
  const {
    pspPartners,
    pspIntegrations,
    externalAccount,
    changeLogs,
    changeLogsPagination,
    externalAccountTopUpOptions,
  } = useAppSelector((state) => state.customersExternalAccounts);
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { users } = useAppSelector((state) => state.administrationDictionaries);

  const {
    currencies: dictionariesCurrencies,
    currenciesList,
    activePermissions,
    paymentTypes,
  } = useAppSelector((state) => state.root);

  const baseInformation =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;

  const organizationStatus =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const {
    externalAccountIsLoading,
    externalAccountIsFetching,
    selectedPartner,
    isOpenPayd,
    integrationType,
    openPaydKeelIntegration,
    balances,
    paymentType,
    caspId,
    selectedExternalOrganizationAccount,
    setBindIsNotAvailable,
    isPrimeVault,
    creationType,
    accountId,
    pspPartnerId,
    control,
    errors,
    setSelectedPartner,
    setValue,
    setIsOpenPayd,
    setIsPrimeVault,
    setIntegrationType,
    setCreationType,
    setBindOrganizationPopupVisible,
    setPanelIsOpen,
    organizationPspExternalAccountsList,
    externalAccountsIsLoading,
    externalAccountsIsFetching,
    bindIsNotAvailalbe,
    primeVaultExternalId,
    setPrimeVaultExternalId,
    balancesIsLoading,
    currencyId,
    panelIsOpen,
    isDirty,
    setDirtyPopupVisible,
    handleClose,
    globalError,
    isLoading,
    createInProgress,
    bindExternalInProgress,
    onSubmitWithIntegration,
    onSubmit,
    dirtyPopupVisible,
    bindOrganizationPopupVisible,
    setSelectedExternalOrganizationAccount,
    availablePaymentTypes,
    activeTab,
    setActiveTab,
    topUpIsLoading,
    topUpIsFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    balancesIsFetching,
    getChangeLogsAction,
    requestInProgress,
    updateCustomerExternalAccountCurrenciesInProgress,
    setIsKeel,
    isKeel,
    topUpOptions,
    setTopUpOptions,
    setSelectedOptionId,
    setDeleteConfirmationVisible,
    optionsErrors,
    setOptionsErrors,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
    deletePaymentAccountsAction,
    deletePaymentAccountsInProgress,
    deleteConfirmationVisible,
  } = useExternalAccountForm({
    externalAccountId,
    externalAccount,
    customerId,
    type,
    pspPartners,
    pspIntegrations,
    baseInformation,
    activePermissions,
    changeLogs,
    paymentTypes,
    dictionariesCurrencies,
    externalAccountTopUpOptions,
  });

  let content = <div />;

  if (externalAccountIsLoading || externalAccountIsFetching) {
    content = <SidePanelLoader />;
  } else if (!externalAccountIsLoading && !externalAccountIsFetching) {
    if (
      (activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
      ) &&
        !externalAccountId) ||
      (externalAccountId &&
        activePermissions?.includes(
          PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
        ))
    ) {
      content = (
        <>
          {externalAccountId &&
            (Boolean(
              externalAccount?.pspIntegrationPaymentAccountsCount &&
              activePermissions?.includes(
                PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
              ),
            ) ||
              activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) && (
              <Tabs
                tabs={[
                  {
                    name: 'Details',
                    value: 'details',
                  },
                  {
                    name: 'Top-up options',
                    value: 'top-up',
                    hidden:
                      Boolean(
                        !externalAccount?.pspIntegrationPaymentAccountsCount,
                      ) ||
                      !activePermissions?.includes(
                        PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
                      ),
                  },
                  {
                    name: 'Change Log',
                    value: 'change-logs',
                    hidden: !activePermissions?.includes(
                      PermissionEnum.AUDIT_LOG_LIST,
                    ),
                  },
                ]}
                activeTab={activeTab}
                onClick={(tab) =>
                  setActiveTab(tab as 'details' | 'top-up' | 'change-logs')
                }
                wrapperClassName={cn.externalAccountTabs}
              />
            )}
          <Activity mode={activeTab === 'details' ? 'visible' : 'hidden'}>
            <ExternalAccountFormContent
              dictionariesCurrencies={dictionariesCurrencies}
              selectedPartner={selectedPartner}
              paymentTypes={paymentTypes}
              externalAccountId={externalAccountId}
              isOpenPayd={isOpenPayd}
              isKeel={isKeel}
              integrationType={integrationType}
              openPaydIntegration={openPaydKeelIntegration}
              balances={balances}
              paymentType={paymentType}
              externalAccount={externalAccount}
              caspValues={casp}
              casp={casp?.map((item) => ({ name: item.name, value: item.id }))}
              caspId={caspId}
              selectedExternalOrganizationAccount={
                selectedExternalOrganizationAccount
              }
              setBindIsNotAvailable={setBindIsNotAvailable}
              isPrimeVault={isPrimeVault}
              creationType={creationType}
              accountId={accountId}
              pspPartnerId={pspPartnerId}
              control={control}
              errors={errors}
              pspPartners={pspPartners}
              setSelectedPartner={setSelectedPartner}
              setValue={setValue}
              setIsOpenPayd={setIsOpenPayd}
              setIsPrimeVault={setIsPrimeVault}
              setIntegrationType={setIntegrationType}
              setCreationType={setCreationType}
              accounts={baseInformation?.accounts}
              pspIntegrationOrganization={
                baseInformation?.pspIntegrationOrganization
              }
              activePermissions={activePermissions}
              setBindOrganizationPopupVisible={setBindOrganizationPopupVisible}
              setPanelIsOpen={setPanelIsOpen}
              organizationPspExternalAccountsList={
                organizationPspExternalAccountsList
              }
              externalAccountsIsLoading={externalAccountsIsLoading}
              externalAccountsIsFetching={externalAccountsIsFetching}
              setSelectedExternalOrganizationAccount={
                setSelectedExternalOrganizationAccount
              }
              bindIsNotAvailalbe={bindIsNotAvailalbe}
              primeVaultExternalId={primeVaultExternalId}
              setPrimeVaultExternalId={setPrimeVaultExternalId}
              balancesIsLoading={balancesIsLoading || balancesIsFetching}
              currencyId={currencyId}
              availablePaymentTypes={availablePaymentTypes}
              setIsKeel={setIsKeel}
            />
          </Activity>
          <Activity mode={activeTab === 'top-up' ? 'visible' : 'hidden'}>
            <>
              {(topUpIsLoading || topUpIsFetching) && <SidePanelLoader />}
              {!topUpIsLoading && (
                <>
                  <div className={cn.topUpOptionsTitleWrapper}>
                    <div className={cn.topUpOptionsTitleWrapperLeft}>
                      <div className={cn.topUpOptionsTitle}>Top-up options</div>
                      <div className={cn.topUpOptionsDescription}>
                        Payment details the customer uses to top up this account
                      </div>
                    </div>
                    {externalAccount?.createType === 'MANUAL' &&
                      activePermissions?.includes(
                        PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_ADD_ACTION,
                      ) && (
                        <ButtonSecondary
                          className={`${cn.createOptionButton} button-white`}
                          icon={<IconExpand />}
                          text="Add option"
                          size={ButtonSize.SMALL}
                          onClick={() => {
                            const nextOptions = (
                              [] as ExternalAccountTopUpOptionFormValues[]
                            ).concat(topUpOptions);

                            nextOptions.unshift({
                              id: `option-${uuid()}`,
                              paymentType: '',
                              currencyId: '',
                              bankAccountHolderName: '',
                              accountNumber: '',
                              bankAddress: '',
                              bic: '',
                              iban: '',
                              thirdPartyReference: '',
                              bankName: '',
                              routingCodeEntries: [],
                            });

                            setTopUpOptions(nextOptions);
                          }}
                        />
                      )}
                  </div>
                  {!topUpOptions?.length && (
                    <div className={cn.emptyTopUpOption}>
                      No top up options yet
                    </div>
                  )}
                  {topUpOptions &&
                    topUpOptions?.length > 0 &&
                    topUpOptions?.map((item) => (
                      <ExternalAccountTopUpOptionItem
                        key={item.id}
                        topUpOption={item}
                        paymentTypes={paymentTypes}
                        currencies={currenciesList}
                        setSelectedOptionId={setSelectedOptionId}
                        setDeleteConfirmationVisible={
                          setDeleteConfirmationVisible
                        }
                        activePermissions={activePermissions}
                        isEditable={
                          externalAccount?.createType === 'MANUAL' &&
                          activePermissions?.includes(
                            PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_EDIT_ACTION,
                          )
                        }
                        newOption={Boolean(item?.id?.includes('option'))}
                        setTopUpOptions={setTopUpOptions}
                        topUpOptions={topUpOptions}
                        optionsError={optionsErrors?.find(
                          (error) => error?.id === item.id,
                        )}
                        optionsErrors={optionsErrors}
                        setOptionsErrors={setOptionsErrors}
                      />
                    ))}
                </>
              )}
            </>
          </Activity>
          <Activity mode={activeTab === 'change-logs' ? 'visible' : 'hidden'}>
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="PSP_INTEGRATION_ACCOUNT"
                  entityId={externalAccountId as string}
                  changeLogsIsLoading={changeLogsIsLoading}
                  changeLogs={changeLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={changeLogsPagination}
                  changeLogsIsFetching={changeLogsIsFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          </Activity>
        </>
      );
    } else {
      content = <PermissionDenied />;
    }
  }

  let buttonText;

  if (
    isLoading ||
    createInProgress ||
    bindExternalInProgress ||
    requestInProgress ||
    updateCustomerExternalAccountCurrenciesInProgress ||
    createPaymentAccountsInProgress ||
    updatePaymentAccountsInProgress
  ) {
    buttonText = 'Loading...';
  } else {
    buttonText = 'Add account';

    if (accountId) {
      buttonText = 'Save';
    }
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title={
          externalAccountId
            ? 'External Account settings'
            : 'Add External Account'
        }
        errorSidepanel={globalError}
        sidepanelExpandedKey="customer-create-external-account"
        footer={
          <>
            {(!externalAccountId ||
              (externalAccountId &&
                organizationStatus !== CustomerStatus.CLOSED)) && (
              <ButtonPrimary
                text={buttonText}

                disabled={
                  (!externalAccountId && !isDirty) ||
                  isLoading ||
                  createInProgress ||
                  bindExternalInProgress ||
                  requestInProgress ||
                  updateCustomerExternalAccountCurrenciesInProgress ||
                  createPaymentAccountsInProgress ||
                  updatePaymentAccountsInProgress ||
                  (creationType === 'bind' &&
                    !activePermissions?.includes(
                      PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
                    ))
                }
                onClick={() =>
                  integrationType === 'with-integration'
                    ? onSubmitWithIntegration()
                    : onSubmit()
                }
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="Delete this option?"
        description=""
        onCancelClick={() => {
          setDeleteConfirmationVisible(false);
          setSelectedOptionId('');
        }}
        onOkClick={deletePaymentAccountsAction}
        cancelButtonText="Cancel"
        okButtonText={
          deletePaymentAccountsInProgress ? 'Loading..' : 'Yes, Delete'
        }
        okButtonDisabled={deletePaymentAccountsInProgress}
        isVisible={deleteConfirmationVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <LinkCustomerForm
        type={type}
        linkExternalCustomerPopupVisible={bindOrganizationPopupVisible}
        setLinkExternalCustomerPopupVisible={setBindOrganizationPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
        selectedPartner={selectedPartner?.id}
        selectedPspIntegrationId={selectedPartner?.pspIntegrationId}
        setExternalPanelIsOpen={setPanelIsOpen}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default ExternalAccountForm;
